/**
 * Утилита для предзагрузки изображений и ресурсов
 * Это улучшает производительность, особенно на мобильных устройствах
 */

// Кэш для уже загруженных изображений
const preloadedImages = new Set();

/**
 * Предзагрузка одного изображения
 * @param {string} src URL изображения для предзагрузки
 * @returns {Promise} Promise, который разрешается, когда изображение загружено
 */
export const preloadImage = (src) => {
  // Если URL не указан или уже загружен, возвращаем
  if (!src || preloadedImages.has(src)) {
    return Promise.resolve();
  }

  // Исправляем путь к изображению, добавляя слеш в начале, если его нет
  const fixedSrc = src.startsWith('/') ? src : `/${src}`;
  
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      preloadedImages.add(src);
      resolve();
    };
    img.onerror = () => {
      // Пробуем альтернативный путь без параметров и с префиксом
      const baseSrc = fixedSrc.split('?')[0];
      const altImg = new Image();
      altImg.onload = () => {
        preloadedImages.add(src);
        resolve();
      };
      altImg.onerror = () => {
        console.warn(`Не удалось предзагрузить изображение: ${src}`);
        // Игнорируем ошибку и решаем Promise успешно, чтобы не блокировать загрузку других изображений
        resolve();
      };
      altImg.src = baseSrc;
    };
    img.src = fixedSrc;
  });
};

/**
 * Предзагрузка группы изображений
 * @param {Array<string>} sources Массив URL изображений
 * @returns {Promise} Promise, который разрешается, когда все изображения загружены
 */
export const preloadImages = (sources = []) => {
  const promises = sources
    .filter(Boolean)
    .map(src => preloadImage(src));
  
  return Promise.all(promises);
};

/**
 * Предзагрузка стилей
 * @param {string} href URL файла стилей
 */
export const preloadStylesheet = (href) => {
  if (!href) return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'style';
  link.href = href;
  document.head.appendChild(link);
};

/**
 * Предзагрузка шрифтов
 * @param {string} href URL файла шрифта
 * @param {string} type Тип шрифта, например 'woff2'
 */
export const preloadFont = (href, type = 'woff2') => {
  if (!href) return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'font';
  link.href = href;
  link.type = `font/${type}`;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
};

/**
 * Предзагрузка критических ресурсов приложения
 */
export const preloadCriticalResources = () => {
  // Основные изображения, которые нужны на главной странице
  try {
    // Получаем базовый путь приложения (/ или /subfolder/)
    const basePath = window.location.pathname.includes('/projects') 
      ? '../' // Если мы на странице проектов, идем на уровень выше
      : '';
      
    const criticalImages = [
      `${basePath}images/projects/default-project.webp`,
      `${basePath}images/projects/project1.webp`,
      `${basePath}images/projects/project2.webp`
    ];

    // Добавляем к изображениям метку времени для обхода кэширования
    const timestampedImages = criticalImages.map(img => `${img}?t=${Date.now()}`);

    // Предзагрузка критических изображений
    console.log('Предзагрузка изображений:', timestampedImages);
    preloadImages(timestampedImages);
  } catch (error) {
    console.error('Ошибка при предзагрузке изображений:', error);
  }
  
  // Предзагрузка шрифтов если необходимо
  // preloadFont('https://fonts.googleapis.com/css?family=Roboto:400,700&display=swap');
};
